import type { FC, PropsWithChildren } from 'react';
import { Suspense, useEffect } from 'react';

import '@opendoor/auth-fe';
// eslint-disable-next-line no-restricted-imports -- Root level import allowed
import { Toast } from '@opendoor/bricks/complex/Toast';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import {
  AddContextualData,
  addDatadogRUMGlobalContext,
  analytics,
  setRUMUser,
} from '@opendoor/observability';
import { useLocation } from '@remix-run/react';

import { LoadingPage } from 'platform/components/LoadingPage';
import { InternalNavA11yAnnouncer } from 'platform/InternalNavA11yAnnouncer';
import { ODTeamBoundary } from 'platform/ODTeamBoundary';

import { useBrowserCompatibilityData } from 'shared/helpers/globals';
import { MobileDeviceContextualData } from 'shared/hooks/MobileDeviceContextualData';
import { usePath } from 'shared/hooks/usePath';

import { useAuth } from './auth-fe';
import { ReauthProvider } from './components/ReauthProvider';
import { TOSModal, TOSProvider } from './components/TOSProvider';
import { useHideDefaultPropsWarnings } from './Error';

/**
 * Adds route and device contextual data to the observability context.
 */
const RouteContextualData: FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation();
  const path = usePath();
  return (
    <AddContextualData data={{ path, pathname }}>
      <MobileDeviceContextualData>{children}</MobileDeviceContextualData>
    </AddContextualData>
  );
};

export const DashboardLayout: FC<PropsWithChildren> = ({ children }) => {
  useHideDefaultPropsWarnings(['ToastContainer']);
  return (
    <Suspense fallback={<LoadingPage />}>
      <ODTeamBoundary team="seller">
        <RouteContextualData>
          <InternalNavA11yAnnouncer />
          <ThemedLite theme="eero">
            <ReauthProvider>
              <AuthContextualData>
                <ODTeamBoundary team="seller">
                  <TOSProvider>
                    {children}
                    <TOSModal />
                  </TOSProvider>
                </ODTeamBoundary>
              </AuthContextualData>
            </ReauthProvider>
          </ThemedLite>
          {/* Toast should only be mounted once at the root */}
          <Toast />
        </RouteContextualData>
      </ODTeamBoundary>
    </Suspense>
  );
};

const AuthContextualData: FC<PropsWithChildren> = ({ children }) => {
  const { user, state } = useAuth();
  useEffect(() => {
    if (!user) {
      return;
    }
    addDatadogRUMGlobalContext({
      hasOpendoorEmail: user.hasOpendoorEmail,
      hasOpendoorInternalAccess: user.hasOpendoorInternalAccess,
    });
    if (user.customerUuid) {
      setRUMUser({ id: user.customerUuid, name: user.fullName, email: user.email });
    }
    analytics.registerWithUserID(user.customerUuid ?? '', { ...user });
  }, [user]);
  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();

  return (
    <AddContextualData
      user={
        user
          ? {
              humanID: user.humanToken,
              email: user.email,
            }
          : undefined
      }
      data={{
        authenticationState: state,
        // This ensures the user data is sent to segment/amplitude
        humanToken: user?.humanToken,
        email: user?.email,
        hasOpendoorEmail: user?.hasOpendoorEmail,
        hasOpendoorInternalAccess: user?.hasOpendoorInternalAccess,
        isMobileNativeWebView: isOpendoorAppEvoWebView,
      }}
      customerContext={user?.customerUuid ? { customerUuid: user.customerUuid } : undefined}
    >
      {children}
    </AddContextualData>
  );
};
